import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { CdkOverlayOrigin, CdkConnectedOverlay, ConnectedPosition } from '@angular/cdk/overlay';
import { IconComponent } from '@outlaw/icons';
import { CountBadgeComponent } from '../count-badge/count-badge.component';
import { Selection } from '../../interfaces';

@Component({
  selector: 'outlaw-selections-menu',
  templateUrl: './selections-menu.component.html',
  styleUrl: './selections-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [NgTemplateOutlet, CdkOverlayOrigin, CdkConnectedOverlay, IconComponent, CountBadgeComponent],
})
export class SelectionsMenuComponent implements OnDestroy {
  @ViewChild('triggerEl') triggerEl!: ElementRef<HTMLElement>;

  selections = input<Selection[]>([]);
  isOpen = input<boolean>(false);
  postUuid = input<string>('');
  mode = input<'overlay' | 'inline'>('overlay');

  selectionToggled = output<Selection>();
  createClicked = output<void>();
  closed = output<void>();
  menuMouseEnter = output<void>();
  menuMouseLeave = output<void>();

  #hoverOpen = signal(false);
  #closeTimer: ReturnType<typeof setTimeout> | null = null;

  effectiveOpen = computed(() => this.isOpen() || this.#hoverOpen());

  inlineStyle = signal<Record<string, string>>({});

  menuPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
  ];

  constructor() {
    effect(() => {
      if (this.mode() === 'inline' && this.effectiveOpen() && this.triggerEl) {
        const rect = this.triggerEl.nativeElement.getBoundingClientRect();
        const spaceAbove = rect.top;
        const spaceBelow = window.innerHeight - rect.bottom;

        if (spaceAbove > spaceBelow) {
          this.inlineStyle.set({
            bottom: `${window.innerHeight - rect.top + 8}px`,
            right: `${window.innerWidth - rect.right}px`,
          });
        } else {
          this.inlineStyle.set({
            top: `${rect.bottom + 8}px`,
            right: `${window.innerWidth - rect.right}px`,
          });
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
    }
  }

  onHoverEnter(): void {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
    this.#hoverOpen.set(true);
    this.menuMouseEnter.emit();
  }

  onHoverLeave(): void {
    this.#closeTimer = setTimeout(() => {
      this.#hoverOpen.set(false);
      this.#closeTimer = null;
    }, 200);
    this.menuMouseLeave.emit();
  }

  isPostInSelection(selection: Selection): boolean {
    return selection.posts_uuids.includes(this.postUuid());
  }

  onToggle(e: Event, selection: Selection): void {
    e.stopPropagation();
    this.selectionToggled.emit(selection);
  }

  onCreate(e: Event): void {
    e.stopPropagation();
    this.createClicked.emit();
  }

  onBackdropClick(): void {
    this.closed.emit();
  }
}
